import {
  SELECT_ACTIVITY_ORG,
  SET_ACTIVITY_FLAGGED_ONLY,
  SET_ACTIVITY_SORT,
  FETCH_ACTIVITY_FLEET_START,
  FETCH_ACTIVITY_FLEET,
  FETCH_ACTIVITY_FLEET_FAILURE,
  SET_ACTIVITY_FLEET_ROW,
  SET_ACTIVITY_SEARCH,
  SELECT_ACTIVITY_DEVICE,
  FETCH_ACTIVITY_ALERTS_START,
  FETCH_ACTIVITY_ALERTS,
  FETCH_ACTIVITY_ALERTS_FAILURE,
  SET_ACTIVITY_ALERT_TYPE,
  MARK_ACTIVITY_ALERTS_READ,
  SET_ACTIVITY_UPDATED_RANGE,
  SET_ACTIVITY_STATE_MACHINE,
  FETCH_ACTIVITY_ALERT_SETTINGS_START,
  FETCH_ACTIVITY_ALERT_SETTINGS,
  FETCH_ACTIVITY_ALERT_SETTINGS_FAILURE,
  UPDATE_ACTIVITY_ALERT_SETTINGS_START,
  UPDATE_ACTIVITY_ALERT_SETTINGS,
  UPDATE_ACTIVITY_ALERT_SETTINGS_FAILURE,
} from "../actions/types";
import { clampAppEnv } from "../utils/environment";

const DEFAULT_ACTIVITY_STATE = {
  organizationId: null,
  flaggedOnly: false,
  sort: null, // null = flagged-first, then staleest-first; see setActivitySort
  search: "",
  updatedFrom: null, // epoch ms, local start of the picked day; null = no lower bound
  updatedTo: null, // epoch ms, local start of the picked day; the day itself is included
  stateMachine: "", // "" = All
  // pins the fleet to the environment this build runs in; no UI control widens it
  env: clampAppEnv(),
  fleet: [],
  fleetCursor: null,
  fleetLoading: false,
  fleetError: null,
  selectedDevice: null,
  alertType: "maintenance",
  alerts: [],
  alertsCursor: null,
  alertsLoading: false,
  alertsError: null,
  optimisticVersion: 0,
  alertSettings: null,
  alertSettingsLoading: false,
  alertSettingsError: null,
  alertSettingsUpdating: false,
  alertSettingsUpdateError: null,
  // optimisticVersion can't be reused here: an unrelated fleet refresh bumping it
  // would drop the modal's only settings fetch, which nothing else would repeat
  alertSettingsRequest: 0,
};

/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = DEFAULT_ACTIVITY_STATE, action) => {
  switch (action.type) {
    case SELECT_ACTIVITY_ORG:
      return {
        ...state,
        organizationId: action.payload.organizationId,
        fleet: [],
        fleetCursor: null,
        fleetLoading: true,
      };
    case SET_ACTIVITY_FLAGGED_ONLY:
      return {
        ...state,
        flaggedOnly: action.payload.flaggedOnly,
        fleet: [],
        fleetCursor: null,
        fleetLoading: true,
      };
    case SET_ACTIVITY_SORT:
      return {
        ...state,
        sort: action.payload.sort,
        fleet: [],
        fleetCursor: null,
        fleetLoading: true,
      };
    case SET_ACTIVITY_SEARCH:
      return {
        ...state,
        search: action.payload.search,
        fleet: [],
        fleetCursor: null,
        fleetLoading: true,
      };
    case SET_ACTIVITY_UPDATED_RANGE:
      return {
        ...state,
        updatedFrom: action.payload.from,
        updatedTo: action.payload.to,
        fleet: [],
        fleetCursor: null,
        fleetLoading: true,
      };
    case SET_ACTIVITY_STATE_MACHINE:
      return {
        ...state,
        stateMachine: action.payload.stateMachine,
        fleet: [],
        fleetCursor: null,
        fleetLoading: true,
      };
    case FETCH_ACTIVITY_FLEET_START:
      return { ...state, fleetLoading: true, fleetError: null };
    case FETCH_ACTIVITY_FLEET: {
      // a background poll that started before an optimistic write must not undo it
      if (
        action.payload.version !== undefined &&
        action.payload.version !== state.optimisticVersion
      ) {
        return state;
      }
      const sameFilter =
        (action.payload.organizationId || null) ===
          (state.organizationId || null) &&
        !!action.payload.flaggedOnly === !!state.flaggedOnly &&
        (action.payload.sort || null) === (state.sort || null) &&
        (action.payload.search || "") === (state.search || "") &&
        (action.payload.updatedFrom || null) === (state.updatedFrom || null) &&
        (action.payload.updatedTo || null) === (state.updatedTo || null) &&
        (action.payload.stateMachine || "") === (state.stateMachine || "");
      // a discarded response always has a newer request behind it - the effect
      // that invalidated it dispatched _START - so leave the loading flag alone
      // rather than flashing the empty state until that newer response lands
      if (!sameFilter) {
        return state;
      }
      return {
        ...state,
        // appending a page is itself a write a poll must not undo: a background
        // fetch already in flight when Load more landed still carries the old
        // version, so bumping here makes its response fall out above
        ...(action.payload.append
          ? { optimisticVersion: (state.optimisticVersion || 0) + 1 }
          : {}),
        fleetLoading: false,
        fleetError: null,
        fleetCursor: action.payload.nextCursor,
        fleet: action.payload.append
          ? [...state.fleet, ...action.payload.items]
          : action.payload.items,
      };
    }
    case FETCH_ACTIVITY_FLEET_FAILURE:
      // a failed background poll is silent: it must not turn the empty state
      // into an error, nor cancel a foreground load that is still in flight
      if (action.payload?.background) {
        return state;
      }
      return { ...state, fleetLoading: false, fleetError: true };
    case SET_ACTIVITY_FLEET_ROW:
      return {
        ...state,
        optimisticVersion: (state.optimisticVersion || 0) + 1,
        fleet: state.fleet.map((row) =>
          row.deviceId === action.payload.row.deviceId
            ? { ...row, ...action.payload.row }
            : row
        ),
        // selectedDevice is a snapshot of the row taken when the modal opened, and
        // the modal reads unreadMaintenanceCount off it to gate "Mark all read" -
        // refreshing the fleet row without it would leave the button stuck enabled
        selectedDevice:
          state.selectedDevice?.deviceId === action.payload.row.deviceId
            ? { ...state.selectedDevice, ...action.payload.row }
            : state.selectedDevice,
      };
    case SELECT_ACTIVITY_DEVICE:
      return {
        ...state,
        selectedDevice: action.payload.device,
        alerts: [],
        alertsCursor: null,
        alertsLoading: true,
        alertsError: null,
        alertType: "maintenance",
        // alertSettingsRequest is not reset here - it only ever increments
        alertSettings: null,
        alertSettingsLoading: false,
        alertSettingsError: null,
        alertSettingsUpdating: false,
        alertSettingsUpdateError: null,
      };
    case FETCH_ACTIVITY_ALERTS_START:
      return { ...state, alertsLoading: true, alertsError: null };
    case FETCH_ACTIVITY_ALERTS: {
      // a background poll that started before an optimistic write must not undo it
      if (
        action.payload.version !== undefined &&
        action.payload.version !== state.optimisticVersion
      ) {
        return state;
      }
      // see FETCH_ACTIVITY_FLEET: a discarded response leaves loading untouched
      if (action.payload.alertType !== state.alertType) {
        return state;
      }
      if (action.payload.deviceUuid !== state.selectedDevice?.deviceId) {
        return state;
      }
      return {
        ...state,
        // see FETCH_ACTIVITY_FLEET: an appended page must survive a poll that
        // was already in flight when Load more landed
        ...(action.payload.append
          ? { optimisticVersion: (state.optimisticVersion || 0) + 1 }
          : {}),
        alertsLoading: false,
        alertsError: null,
        alertsCursor: action.payload.nextCursor,
        alerts: action.payload.append
          ? [...state.alerts, ...action.payload.items]
          : action.payload.items,
      };
    }
    case FETCH_ACTIVITY_ALERTS_FAILURE:
      // see FETCH_ACTIVITY_FLEET_FAILURE
      if (action.payload?.background) {
        return state;
      }
      return { ...state, alertsLoading: false, alertsError: true };
    case SET_ACTIVITY_ALERT_TYPE:
      return {
        ...state,
        alertType: action.payload.alertType,
        alerts: [],
        alertsCursor: null,
        alertsLoading: true,
        alertsError: null,
      };
    case MARK_ACTIVITY_ALERTS_READ: {
      const { ids, all, alertType } = action.payload;
      return {
        ...state,
        optimisticVersion: (state.optimisticVersion || 0) + 1,
        alerts: state.alerts.map((alert) => {
          if (ids && ids.includes(alert._id)) {
            return { ...alert, read: true };
          }
          if (all && alert.alertType === alertType) {
            return { ...alert, read: true };
          }
          return alert;
        }),
      };
    }
    case FETCH_ACTIVITY_ALERT_SETTINGS_START:
      return {
        ...state,
        alertSettingsRequest: (state.alertSettingsRequest || 0) + 1,
        alertSettingsLoading: true,
        alertSettingsError: null,
      };
    case FETCH_ACTIVITY_ALERT_SETTINGS: {
      // a stale requestId or a response for a device that is no longer open
      if (
        action.payload.requestId !== state.alertSettingsRequest ||
        action.payload.deviceUuid !== state.selectedDevice?.deviceId
      ) {
        return state;
      }
      return {
        ...state,
        alertSettings: action.payload.settings,
        alertSettingsLoading: false,
      };
    }
    case FETCH_ACTIVITY_ALERT_SETTINGS_FAILURE: {
      // see FETCH_ACTIVITY_ALERT_SETTINGS
      if (
        action.payload.requestId !== state.alertSettingsRequest ||
        action.payload.deviceUuid !== state.selectedDevice?.deviceId
      ) {
        return state;
      }
      return { ...state, alertSettingsLoading: false, alertSettingsError: true };
    }
    case UPDATE_ACTIVITY_ALERT_SETTINGS_START: {
      // a stale START for a device the user has since navigated away from
      if (action.payload.deviceUuid !== state.selectedDevice?.deviceId) {
        return state;
      }
      return {
        ...state,
        alertSettingsUpdating: true,
        alertSettingsUpdateError: null,
      };
    }
    case UPDATE_ACTIVITY_ALERT_SETTINGS: {
      const { deviceUuid, settings } = action.payload;
      const alertsMuted = settings?.muted === true;
      const isSelected = deviceUuid === state.selectedDevice?.deviceId;
      return {
        ...state,
        optimisticVersion: (state.optimisticVersion || 0) + 1,
        fleet: state.fleet.map((row) =>
          row.deviceId === deviceUuid ? { ...row, alertsMuted } : row
        ),
        // only the open modal's own display state changes; a save for another
        // device only updates its fleet row above
        ...(isSelected
          ? {
              selectedDevice: { ...state.selectedDevice, alertsMuted },
              alertSettings: settings,
              alertSettingsRequest: (state.alertSettingsRequest || 0) + 1,
              alertSettingsLoading: false,
              alertSettingsError: null,
              alertSettingsUpdating: false,
              alertSettingsUpdateError: null,
            }
          : {}),
      };
    }
    case UPDATE_ACTIVITY_ALERT_SETTINGS_FAILURE: {
      // see UPDATE_ACTIVITY_ALERT_SETTINGS_START
      if (action.payload.deviceUuid !== state.selectedDevice?.deviceId) {
        return state;
      }
      return {
        ...state,
        alertSettingsUpdating: false,
        alertSettingsUpdateError: true,
      };
    }
    default:
      return state;
  }
};
